import React, { useEffect, useMemo, useState } from "react";
import { Country } from "country-state-city";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { api } from "../../app/Api/ApiSlice";

// ---------------- PLANS ----------------
const plans = [
  { id: 2, name: "National", price: "ksh 1200 / month", countryLimit: 1, features: ["1 country", "Basic support", "Multiple admins"] },
  { id: 3, name: "International", price: "ksh 1800 / month", countryLimit: 10, features: ["Up to 10 countries", "Priority support", "Multiple admins"] },
  { id: 4, name: "Global", price: "ksh 2800 / month", countryLimit: Infinity, features: ["All countries included", "24/7 support"] }
];

// ---------------- CATEGORIES ----------------
const categoryOptions = [
  "homecare", "groceries", "furnitures", "beauty", "fashions", "stationery",
  "food & restaurants", "phone & accessories", "computer & accessories", "cars & accessories",
  "vacations & holidays", "videos & photography", "gifts", "construction & building",
  "sports & fitness", "farmproduce", "agriculture & accessories", "financial Services",
  "health Services", "education & equipments", "entertainment", "event Facilities",
  "hobbies", "manufacturing Machineries", "IT & software", "security Services", "others"
];

export default function CreateShopWithPlans() {
  const navigate = useNavigate();

  // -------- BASIC INFO --------
  const [shopName, setShopName] = useState("");
  const [tagline, setTagline] = useState("");
  const [description, setDescription] = useState("");
  const [currency, setCurrency] = useState("");
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false)

  // -------- ADDRESS --------
  const [country, setCountry] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [street, setStreet] = useState("");
  const [building, setBuilding] = useState("");
  const [room, setRoom] = useState("");

  // -------- CONTACT --------
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [phone, setPhone] = useState("");
  const [phone2, setPhone2] = useState("");

  // -------- PLAN + COUNTRIES --------
  const [selectedPlanId, setSelectedPlanId] = useState(1);
  const [allCountries, setAllCountries] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);

  const currentPlan = useMemo(() => plans.find(p => p.id === selectedPlanId), [selectedPlanId]);
  const countryLimit = currentPlan?.countryLimit;

  useEffect(() => {
    setAllCountries(Country.getAllCountries().map(c => ({ value: c.isoCode, label: c.name })));
  }, []);

  const handlePlanChange = (id) => {
    const plan = plans.find(p => p.id === id);
    setSelectedPlanId(id);
    if (plan.countryLimit === Infinity) setSelectedCountries([]);
    else if (selectedCountries.length > plan.countryLimit) setSelectedCountries(prev => prev.slice(0, plan.countryLimit));
  };

  const toggleCountry = (country) => {
    const exists = selectedCountries.some(c => c.value === country.value);
    if (exists) return setSelectedCountries(prev => prev.filter(c => c.value !== country.value));
    if (selectedCountries.length >= countryLimit) return toast.error(`Limit reached (${countryLimit})`);
    setSelectedCountries(prev => [...prev, country]);
  };

  const addCategory = (cat) => {
    if (categories.includes(cat)) return toast.error("Category already added");
    if (categories.length >= 3) return toast.error("Maximum 3 categories allowed");
    setCategories(prev => [...prev, cat]);
  };

  const removeCategory = (cat) => setCategories(prev => prev.filter(c => c !== cat));

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!shopName || !description || !email || !phone || !building) {
      return toast.error("Fill all required fields");
    }

    if (categories.length === 0) return toast.error("Select at least one category");
    if (countryLimit !== Infinity && selectedCountries.length === 0) return toast.error("Select at least one country");

    const payload = {
      name: shopName,
      tagline,
      description,
      currency,
      categories,
      address: { country, state, city, street, building, room },
      contacts: { email, website, phone, phone2, },
      planId: selectedPlanId,
      countries: countryLimit === Infinity ? "ALL" : selectedCountries.map(c => c.value)
    };

    try {
      setIsLoading(true)
      const res = await api.post("/stores", payload);
      setIsLoading(false)
      toast.success("Store created successfully");
      navigate(`/b/${res.data.name}`);
    } catch (error) {
      setIsLoading(false)
      console.log(error)
      toast.error(error.response.data.message||"Error, business not saved");
    }
  };

  return (
    <div className="create-shop-page">
      <form className="card" onSubmit={handleSubmit}>
        <h2>Create Store</h2>

        <input placeholder="Store name *" value={shopName} onChange={e => setShopName(e.target.value)} />
        <input placeholder="Tagline" value={tagline} onChange={e => setTagline(e.target.value)} />
        <textarea placeholder="Description *" value={description} onChange={e => setDescription(e.target.value)} />

        <h3>Categories (max 3)</h3>
        <select onChange={e => addCategory(e.target.value)} defaultValue="">
          <option value="" disabled>Select category</option>
          {categoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        <div className="chips">
          {categories.map(c => (
            <span key={c} className="chip" onClick={() => removeCategory(c)}>{c} ×</span>
          ))}
        </div>

        <h3>Select Plan</h3>
        <div className="plans">
          {plans.map(p => (
            <div key={p.id} className={`plan ${selectedPlanId === p.id ? "active" : ""}`} onClick={() => handlePlanChange(p.id)}>
              <h4>{p.name}</h4>
              <strong>{p.price}</strong>
              <ul>{p.features.map(f => <li key={f}>{f}</li>)}</ul>
            </div>
          ))}
        </div>

        <h3>Countries</h3>
        {countryLimit === Infinity ? (
          <p className="info">All countries included</p>
        ) : (
          <div className="countries">
            {allCountries.map(c => (
              <button type="button" key={c.value} className={selectedCountries.some(s => s.value === c.value) ? "country active" : "country"} onClick={() => toggleCountry(c)}>
                {c.label}
              </button>
            ))}
          </div>
        )}

        <input placeholder="Currency" value={currency} onChange={e => setCurrency(e.target.value)} />
        <h3>Address</h3>
        <input placeholder="Country" value={country} onChange={e => setCountry(e.target.value)} />
        <input placeholder="State" value={state} onChange={e => setState(e.target.value)} />
        <input placeholder="City" value={city} onChange={e => setCity(e.target.value)} />
        <input placeholder="Street" value={street} onChange={e => setStreet(e.target.value)} />
        <input placeholder="Building *" value={building} onChange={e => setBuilding(e.target.value)} />
        <input placeholder="Room" value={room} onChange={e => setRoom(e.target.value)} />

        <h3>Contact</h3>
        <input placeholder="Email *" value={email} onChange={e => setEmail(e.target.value)} />
        <input placeholder="Website" value={website} onChange={e => setWebsite(e.target.value)} />
        <input placeholder="Phone *" value={phone} onChange={e => setPhone(e.target.value)} />
        <input placeholder="Phone 2" value={phone2} onChange={e => setPhone2(e.target.value)} />
        <label style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
          <input type="checkbox" required />
          <span>
            By checking this box, I acknowledge that I have read and fully understood the Onnize Seller Policy Agreement
            and agree to be legally bound by its terms. I accept full responsibility for payment collection, order fulfillment,
            and delivery, and acknowledge that Onnize bears no liability for unpaid transactions, failed deliveries, or disputes
            arising between buyers and sellers.
          </span>
        </label>


        <button className="primary" disabled={isLoading}>Create Store</button>
      </form>
    </div>
  );
}
